import React, { type FC, useCallback } from 'react'
import { Link, type SxProps, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../utils'

import {
    canServiceBeDisabled,
    Capability,
    createIsAuthenticatedWithCapabilitiesSelector,
    type CybusService,
    NotificationSeverity,
    selectDeletableLoadedServices,
    selectDisablableLoadedServices,
    selectServicesPage,
    type StatusType,
    Translation,
} from '../../../domain'

import { DetailsHeader, Hyphen, Status, useClearTableSelection } from '../common'
import { createFormatter, FormattedTranslation, useTranslator } from '../Internationalization'
import { RedirectingResourcesTable } from '../Resources'
import { RelativeRoutePathWithId, useRedirectionWithId } from '../routing'
import { useAppState, useAppUsecase, useAppUsecases } from '../State'
import { AddOrUpdateModal } from './AddOrUpdate'
import { BulkDeletionModal, BulkDisablingModal } from './Bulk'
import { UploadServiceButton } from './Buttons'
import { ServiceLinks } from './Links'

const Modals: FC = () => {
    const clear = useClearTableSelection()
    const { toastUsecase } = useAppUsecases()
    const translator = useTranslator()
    /**
     * `undefined` when the user isn't authorized to view a service's page - mirrors
     * `PermissionedLink`'s own fallback (rendering plain, non-clickable text) by simply omitting
     * the toast's action in that case, rather than offering a link that would go nowhere.
     */
    const redirectToService = useRedirectionWithId<string>(RelativeRoutePathWithId.SERVICE, (id) => id)

    return (
        <>
            <BulkDeletionModal resourceSelector={selectDeletableLoadedServices} onConfirmed={clear} />
            <BulkDisablingModal resourceSelector={selectDisablableLoadedServices} onConfirmed={clear} />

            <AddOrUpdateModal
                mode="CREATE"
                onConfirmed={(id) =>
                    toastUsecase.show({
                        severity: NotificationSeverity.INFO,
                        title: id,
                        ...(redirectToService &&
                            { action: { label: translator.formatTranslation(Translation.VIEW_SERVICE), onClick: () => redirectToService(id) } }),
                    })}
            />
        </>
    )
}

const selectCanInstallServices = createIsAuthenticatedWithCapabilitiesSelector(
    Capability.SERVICES_PARAMETERS_READ,
    Capability.SERVICES_CREATE_OR_UPDATE,
    Capability.SERVICE_COMMISSIONING_FILE_READ
)
const selectCanManageServices = createIsAuthenticatedWithCapabilitiesSelector(Capability.SERVICES_MANAGE)

const buttonStyle: SxProps<Theme> = { cursor: 'pointer' }

const NoServicesMessage: FC = () => {
    const canInstallServices = useAppState(selectCanInstallServices)
    const addServiceUsecase = useAppUsecase('addServiceUsecase')
    const link = createFormatter(Link, { sx: buttonStyle, onClick: () => addServiceUsecase.startCreation() })

    return <FormattedTranslation id={Translation.NO_SERVICES_INSTALLED} values={{ canInstallServices, link }} />
}

const HeaderActions: FC = () => {
    const addService = useAppUsecase('addServiceUsecase')

    const canInstallServices = useAppState(selectCanInstallServices)

    const startServiceUpload = useCallback(() => addService.startCreation(), [addService])

    return (
        <>
            <UploadServiceButton onClick={startServiceUpload} disabled={!canInstallServices} />
        </>
    )
}

export const Table: FC = () => {
    const translation = useTranslator()
    const customCellRender = (status: StatusType): JSX.Element => <Status status={status} />

    return (
        <>
            <DetailsHeader
                title={[Translation.SERVICE, { count: 0 }]}
                titleActions={<HeaderActions />}
                description={[Translation.SERVICES_DETAILS_DOCUMENTATION_TITLE, Translation.SERVICES_DETAILS_DOCUMENTATION_BODY]}
            />
            <RedirectingResourcesTable<'manageServicesUsecase', CybusService, CybusService, 'bulkDeleteServicesUsecase' | 'bulkDisableServicesUsecase'>
                subscriptionUsecase="manageServicesUsecase"
                data={selectServicesPage}
                hasManagementCapabilitiesSelector={selectCanManageServices}
                dataTableMapper={(r) => r}
                columns={{
                    id: { label: Translation.SERVICE_ID, sort: true },
                    name: { label: Translation.NAME, sort: true },
                    version: { label: Translation.VERSION, sort: true },
                    links: {
                        label: translation.formatTranslation(Translation.LINK, { count: 2 }),
                        customCellRender: (links) => (isArrayNotEmpty(links) ? <ServiceLinks links={links} /> : <Hyphen />),
                    },
                    status: { label: Translation.STATUS, customCellRender, sort: true },
                    deviation: { label: Translation.DEVIATION, customCellRender },
                }}
                redirectOnRowclick={RelativeRoutePathWithId.SERVICE}
                selection={{
                    DELETE: { usecase: 'bulkDeleteServicesUsecase' },
                    DISABLE: { usecase: 'bulkDisableServicesUsecase', filter: canServiceBeDisabled },
                    resource: 'id',
                    usecaseMethod: 'selectServices',
                }}
                translations={{ emptyTable: <NoServicesMessage />, paginationDataType: 'SERVICE' }}
            >
                <Modals />
            </RedirectingResourcesTable>
        </>
    )
}
